import { canOpenAdmin } from "../lib/roleHome";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  CheckCircle2,
  MapPin,
  LogOut,
  Shield,
  RefreshCw,
  Users,
  ChevronRight,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import AlertDetailDialog from "./AlertDetailDialog";
import { MyTasksCard } from "./TasksTab";
import PagerFeedCard from "./PagerFeedCard";
import DeviceStatusCard from "./DeviceStatusCard";
import AlertStatsRow from "./AlertStatsRow";
import DepartmentQueue from "./DepartmentQueue";

function severityColor(s) {
  if (s === "emergency") return { border: "#B6463A", bg: "#FDECE9", text: "#98392F" };
  if (s === "assist") return { border: "#D28D38", bg: "#FDF3E3", text: "#8B5A20" };
  return { border: "#4A7C59", bg: "#EAF3EC", text: "#2F5940" };
}

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function StaffDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [alerts, setAlerts] = useState([]);
  const [showOlder, setShowOlder] = useState(false);   // open alerts older than 72 h, on request only
  const [locations, setLocations] = useState([]);
  const [stats, setStats] = useState({ active: 0, acknowledged: 0, resolved_24h: 0, emergency_active: 0 });
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState(null);
  const [insightSummary, setInsightSummary] = useState({ concern: 0, watch: 0, info: 0, total: 0 });

  const fetchAll = async () => {
    try {
      const [aRes, lRes, sRes, iRes] = await Promise.all([
        Promise.all([api.get("/alerts/feed"), showOlder ? api.get("/alerts/feed", { params: { scope: "older" } }) : { data: [] }])
          .then(([cur, old]) => ({ data: [...cur.data, ...old.data.map((a) => ({ ...a, older: true }))] })),
        api.get("/locations/latest"),
        api.get("/alerts/stats"),
        api.get("/insights/summary").catch(() => ({ data: null })),
      ]);
      setAlerts(aRes.data);
      setLocations(lRes.data);
      setStats(sRes.data);
      if (iRes.data) setInsightSummary(iRes.data);
    } catch (e) {
      // stay silent on poll errors
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 3000);
    return () => clearInterval(t);
  }, [showOlder]); // eslint-disable-line react-hooks/exhaustive-deps

  // /staff?alert=<id> (from Operations Overview / Admin) opens that event's
  // detail + close-out directly, not just the board.
  useEffect(() => { const a = sp.get("alert"); if (a) setDetailId(a); }, [sp]);

  const acknowledge = async (id) => {
    try {
      await api.post(`/alerts/${id}/acknowledge`);
      toast.success("Acknowledged");
      fetchAll();
    } catch {
      toast.error("Could not acknowledge");
    }
  };
  const resolve = async (id) => {
    try {
      await api.post(`/alerts/${id}/resolve`);
      toast.success("Resolved");
      fetchAll();
    } catch {
      toast.error("Could not resolve");
    }
  };

  const answerLiveLine = async (id) => {
    try {
      await api.post(`/alerts/${id}/live-line/answer`, { state: "connected" });
      toast.success("Connected — live transcript below the resident's card");
      fetchAll();
    } catch {
      toast.error("Could not connect");
    }
  };

  const generateMockLocations = async () => {
    try {
      const { data } = await api.post("/locations/mock/generate");
      toast.success(`Simulated ${data.generated} location pings`);
      fetchAll();
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <div className="min-h-screen bg-caos-bone">
      {/* Top bar */}
      <header className="border-b border-caos-line bg-caos-bone sticky top-0 z-30">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-y-2 px-4 sm:px-6 py-4">
          <div className="flex items-center gap-6">
            <Link to="/" data-testid="staff-home-link" className="text-xl">
              <span className="font-display font-bold tracking-tighter text-caos-forest">CAOS</span>
              <span className="font-display font-light text-caos-forest">Care</span>
            </Link>
            <span className="text-caos-mute text-sm whitespace-nowrap">· Staff Dashboard</span>
          </div>
          <div className="flex items-center gap-3 overflow-x-auto max-w-full">
            {canOpenAdmin(user) && (
              <Link to="/operations" data-testid="nav-admin">
                <Button variant="outline" className="border-2 h-10 rounded-full">
                  <Shield className="w-4 h-4 mr-2" /> Admin
                </Button>
              </Link>
            )}
            <span className="text-sm text-caos-mute hidden md:block" data-testid="staff-user-name">
              {user?.name}{user?.role !== "admin" && <span className="ml-1 text-xs uppercase tracking-wider">({user?.role})</span>}
            </span>
            <Button
              variant="outline"
              onClick={async () => {
                await logout();
                nav("/login");
              }}
              data-testid="staff-logout-btn"
              className="border-2 h-10 rounded-full"
            >
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <AlertStatsRow stats={stats} insightSummary={insightSummary} />

        {/* Resident requests routed to nursing (Aria, room screen, front desk).
            Help calls from the pendant/help button stay in Live alerts. */}
        {(user?.department === "nursing" || ["owner", "admin"].includes(user?.role)) && (
          <div className="mb-8">
            <DepartmentQueue department="nursing" title="Nursing requests" adminMode={["owner", "admin"].includes(user?.role)} />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Alerts */}
          <section className="lg:col-span-3">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl font-medium text-caos-forest">Live alerts</h2>
              <Button variant="ghost" onClick={fetchAll} data-testid="refresh-alerts-btn">
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
            </div>

            {stats.older_open > 0 && (
              <div className="text-xs text-caos-mute bg-[#F3F1EE] rounded-lg p-3 mb-3 flex flex-wrap items-center gap-2" data-testid="alerts-older-banner">
                {showOlder
                  ? <>Showing {alerts.filter((a) => a.older).length} of {stats.older_open} older open alert{stats.older_open === 1 ? "" : "s"} too, newest first (opened more than 3 days ago; marked "older"). The full list is on the Alerts board.</>
                  : <>{stats.older_open} older open alert{stats.older_open === 1 ? " is" : "s are"} hidden (opened more than 3 days ago, mostly device tests). Nothing was closed.</>}
                <Button size="sm" variant="outline" className="border-2 h-7" onClick={() => setShowOlder((v) => !v)} data-testid="alerts-older-toggle">{showOlder ? "Hide older" : "Show older"}</Button>
              </div>
            )}
            <div className="space-y-3" data-testid="alerts-list">
              {!loading && alerts.length === 0 && (
                <Card className="p-10 text-center border-caos-line">
                  <CheckCircle2 className="w-10 h-10 text-caos-moss mx-auto" />
                  <p className="mt-3 font-display text-lg text-caos-forest">All clear. No current alerts.</p>
                </Card>
              )}
              {alerts.map((a) => {
                const c = severityColor(a.severity);
                const escLevel = a.escalation_level || 0;
                return (
                  <Card
                    key={a.alert_id}
                    data-testid={`alert-card-${a.alert_id}`}
                    className={`p-5 border-2 relative cursor-pointer hover:shadow-md transition-shadow ${a.severity === "emergency" && a.status === "active" ? "caos-alert-emergency" : ""}`}
                    style={{ borderLeftColor: c.border, borderLeftWidth: 6, background: "#fff" }}
                    onClick={() => setDetailId(a.alert_id)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge
                            data-testid={`alert-sev-${a.alert_id}`}
                            style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}` }}
                            className="uppercase tracking-wider font-bold"
                          >
                            {a.severity}
                          </Badge>
                          <Badge variant="outline" className="uppercase tracking-wider text-xs">
                            {a.status}
                          </Badge>
                          {a.older && <Badge className="bg-caos-mute/20 text-caos-mute text-[10px] uppercase" data-testid={`alert-older-${a.alert_id}`}>older</Badge>}
                          {escLevel > 0 && (
                            <Badge
                              data-testid={`esc-${a.alert_id}`}
                              className={`text-white uppercase tracking-wider text-xs font-bold flex items-center gap-1 ${
                                escLevel === 1
                                  ? "bg-caos-amber"
                                  : escLevel === 2
                                  ? "bg-[#c8662b]"
                                  : "bg-caos-terracotta"
                              }`}
                            >
                              <TrendingUp className="w-3 h-3" /> Escalated Lv{escLevel}
                            </Badge>
                          )}
                          <span className="text-caos-mute text-sm">{timeAgo(a.created_at)}</span>
                          {a.aria_state && a.aria_state !== "dormant" && (
                            <Badge variant="outline" className="text-xs uppercase tracking-wider" data-testid={`aria-state-${a.alert_id}`}>
                              Aria {a.aria_state.replace("_", " ")}
                            </Badge>
                          )}
                        </div>
                        <h3 className="font-display text-xl font-medium text-caos-forest mt-2">
                          {a.resident_name || "Unknown resident"}
                        </h3>
                        <p className="text-caos-mute text-sm mt-1">
                          Room {a.room || "?"} · {a.zone || "Location unknown"} · via {a.triggered_by.replace("_", " ")}
                          {a.press_count > 1 && <span className="font-semibold text-caos-ink"> · {a.press_count} presses</span>}
                        </p>
                        {a.presses?.length > 1 && (
                          <p className="text-caos-mute text-xs mt-1" data-testid={`press-history-${a.alert_id}`}>
                            {a.presses.map((p) => timeAgo(p.at)).join(" · ")}
                          </p>
                        )}
                        {a.pattern_footnote && (
                          <p className="text-caos-mute text-xs mt-1 italic">{a.pattern_footnote}</p>
                        )}
                        {a.silence_after_invite && (
                          <p className="text-caos-amber text-xs mt-1 font-semibold uppercase tracking-wider">Still waiting — no response yet</p>
                        )}
                        {a.message && (
                          <p className="text-caos-ink/70 mt-2 italic">"{a.message}"</p>
                        )}
                        {a.latest_escalation_reason && (
                          <div
                            className="mt-2 p-2.5 rounded-lg bg-caos-terracotta/10 border border-caos-terracotta"
                            data-testid={`escalation-${a.alert_id}`}
                          >
                            <p className="text-caos-terracotta-dark font-semibold text-sm">
                              Reports: "{a.latest_escalation_reason}"
                            </p>
                            <p className="text-caos-mute text-xs mt-0.5">
                              {(a.escalation_source || "AI").replace("_", " ")} escalated
                              {a.original_severity && a.original_severity !== a.severity
                                ? ` ${a.original_severity} → ${a.severity}` : ` to ${a.severity}`}
                              {" · Care/Nursing "}
                              {a.dispatch_status === "delivered"
                                ? "page delivered"
                                : a.dispatch_status === "accepted"
                                ? "paged"
                                : a.dispatch_status === "failed"
                                ? "PAGE FAILED"
                                : a.dispatch_status === "requested"
                                ? "page pending"
                                : "requested"}
                            </p>
                          </div>
                        )}
                        {a.acknowledged_by && (
                          <p className="text-caos-mute text-xs mt-2">Acknowledged by {a.acknowledged_by}</p>
                        )}
                        {a.live_line_state === "ringing" && (
                          <div className="mt-3 p-3 rounded-lg bg-caos-terracotta/10 border-2 border-caos-terracotta flex items-center justify-between gap-3" data-testid={`live-line-ringing-${a.alert_id}`}>
                            <span className="text-caos-terracotta font-bold text-sm uppercase tracking-wider">Live line ringing</span>
                            <Button
                              onClick={(e) => { e.stopPropagation(); answerLiveLine(a.alert_id); }}
                              data-testid={`answer-btn-${a.alert_id}`}
                              className="bg-caos-terracotta hover:bg-caos-terracotta/90 text-white h-9"
                            >
                              Answer
                            </Button>
                          </div>
                        )}
                        {a.live_line_state === "connected" && (
                          <p className="text-caos-moss text-xs mt-2 font-semibold uppercase tracking-wider">Live line connected</p>
                        )}
                      </div>
                      <div className="flex flex-col gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                        {a.status === "active" && (
                          <Button
                            onClick={() => acknowledge(a.alert_id)}
                            data-testid={`ack-btn-${a.alert_id}`}
                            className="bg-caos-amber hover:bg-caos-amber/90 text-white"
                          >
                            Acknowledge
                          </Button>
                        )}
                        <Button
                          onClick={() => setDetailId(a.alert_id)}
                          data-testid={`detail-btn-${a.alert_id}`}
                          variant="outline"
                          className="border-2"
                        >
                          Details <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* Locations */}
          <section className="lg:col-span-2 space-y-6">
            <DeviceStatusCard />

            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl font-medium text-caos-forest">Live locations</h2>
              <Button
                variant="ghost"
                onClick={generateMockLocations}
                data-testid="mock-location-btn"
                title="Simulate mesh network pings"
              >
                <Users className="w-4 h-4 mr-2" /> Simulate
              </Button>
            </div>

            <Card className="border-caos-line bg-white overflow-hidden">
              <div className="divide-y divide-caos-line" data-testid="location-list">
                {locations.map((l) => {
                  // Admin/owner drills into the resident record; a plain nurse
                  // has no resident-detail surface, so their row stays a plain
                  // <div> (not styled like a control).
                  const drill = ["owner", "admin"].includes(user?.role) && l.resident_id;
                  const inner = (
                    <>
                      <div>
                        <p className="font-semibold text-caos-forest">{l.resident_name}</p>
                        <p className="text-caos-mute text-sm">Room {l.room}</p>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-2 justify-end">
                          <MapPin className="w-4 h-4 text-caos-forest" />
                          <span className="font-semibold text-caos-forest">{l.zone || "Not seen yet"}</span>
                        </div>
                        <p className="text-xs text-caos-mute">{l.last_seen ? timeAgo(l.last_seen) : "—"}</p>
                      </div>
                    </>
                  );
                  const cls = `p-4 flex items-center justify-between ${drill ? "hover:bg-caos-ambient/50 cursor-pointer transition-colors" : ""}`;
                  return drill ? (
                    <Link key={l.resident_id} to={`/admin?tab=residents&resident=${l.resident_id}`} data-testid={`loc-row-${l.resident_id}`} className={cls}>{inner}</Link>
                  ) : (
                    <div key={l.resident_id} data-testid={`loc-row-${l.resident_id}`} className={cls}>{inner}</div>
                  );
                })}
                {locations.length === 0 && (
                  <div className="p-6 text-center text-caos-mute">No residents yet.</div>
                )}
              </div>
            </Card>

            <MyTasksCard />
            <PagerFeedCard />
          </section>
        </div>
      </div>

      <AlertDetailDialog
        alertId={detailId}
        open={!!detailId}
        onOpenChange={(o) => { if (!o) setDetailId(null); }}
        onChanged={fetchAll}
      />
    </div>
  );
}

